import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {Feature, VehicleInfo} from '@di-shared/api/di/__generated';
import {PermissionTypeKr, RoleType} from '@di-shared/api/di/type';
import {BrandType} from '@shared/cci/features/const/environment';
import environmentStore from '@di-shared/features/environment/environmentStore';

export enum SeoulMileageSupportedType {
  NOT_SUPPORTED,
  SUPPORTED_NOT_AGREE,
  SUPPORTED_AGREE,
}

export default function useAvailableComponentKr({vin}: {vin: string}) {
  const puid = environmentStore.getState().puid;

  const {data: vehicleInfo, refetch: refetchAvailableComponent} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicle.getVehicle), vin, puid],
    queryFn: () => diApi.vehicle.getVehicle({vin, puid}),
    select: (data: VehicleInfo) => data,
    refetchOnWindowFocus: true, // permission 은 경우에 따라 민감할 수 있어서, 가능하면 화면에 포커스 올떄마다 새로 고치게 함
  });

  function checkAvailableComponent(permissionType: PermissionTypeKr) {
    let isAvailableComponent;
    let canRole;
    let brand;
    switch (permissionType) {
      // 보험 혜택
      case PermissionTypeKr.UBI:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 차량인증등급
      case PermissionTypeKr.CPO:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        brand = import.meta.env.VITE_BRAND;
        isAvailableComponent = canRole && brand !== BrandType.KIA && isSupportFeature(permissionType);
        break;
      // 소모품
      case PermissionTypeKr.CONSUMABLES:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 차량진단
      case PermissionTypeKr.DIAGNOSTICS:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 주행기록
      case PermissionTypeKr.TRIPS:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 안전운전점수
      case PermissionTypeKr.SAFETY_SCORE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 연비전비
      case PermissionTypeKr.TRIP_FUEL:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 브레이크패드
      case PermissionTypeKr.BRAKE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 타이어공기압
      case PermissionTypeKr.TIRE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 엔진오일
      case PermissionTypeKr.ENGINE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 배터리
      case PermissionTypeKr.BATTERY_LOW:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 주유
      case PermissionTypeKr.REFUEL:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 연비
      case PermissionTypeKr.FUEL_EFFICIENCY:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // EV BaaS
      case PermissionTypeKr.BATTERY_PFRM_MNG_DI_SERVICE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER || vehicleInfo.role.code === RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 서울시에코마일리지
      case PermissionTypeKr.SEOUL:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 캐피탈, 캐피탈 제3자동의
      case PermissionTypeKr.FINANCIAL_MANAGEMENT:
      case PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD:
        // 케이스 4만 아니면 캐피탈 사용 가능
        canRole = vehicleInfo.role.code !== RoleType.SECONDARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // EV BaaS 현대캐피탈 특화 정보
      case PermissionTypeKr.BATTERY_PFRM_MNG_SERVICE:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        isAvailableComponent = canRole && isSupportFeature(permissionType);
        break;
      // 홈 최근 주행기록
      case PermissionTypeKr.HOME_RECENT:
        canRole = vehicleInfo.role.code === RoleType.PRIMARY_USER;
        isAvailableComponent = canRole;
        break;
      // 주행기록 최근 주행기록
      case PermissionTypeKr.TRIPS_RECENT:
        canRole =
          vehicleInfo.role.code === RoleType.UNREGISTERED_SPEC ||
          vehicleInfo.role.code === RoleType.PRIMARY_USER ||
          vehicleInfo.role.code === RoleType.SECONDARY_USER;
        // 간략형(case2 || case3 && 주행기록 동의일 이전 날짜 || case4), 추가정보형(case3 && 주행기록 동의일 이후 날짜)은 컴포넌트에서 분기처리
        isAvailableComponent = canRole;
        break;
    }
    return isAvailableComponent;
  }

  function isSupportFeature(permissionType: PermissionTypeKr) {
    return vehicleInfo.permissions[0].features.some((permission) => permission.id === permissionType);
  }

  function isAgreeFeature(permissionType: PermissionTypeKr) {
    const feature = vehicleInfo.permissions[0].features.find((permission) => permission.id === permissionType);
    return !feature?.optionalYn || !!feature?.agreedAt;
  }

  function getSeoulMileageSupportedType(permissionFeatures: Feature[]) {
    const seoulPermission = permissionFeatures.find(({id}) => id === PermissionTypeKr.SEOUL);
    if (!seoulPermission) {
      return SeoulMileageSupportedType.NOT_SUPPORTED;
    }
    if (!seoulPermission.agreedAt) {
      return SeoulMileageSupportedType.SUPPORTED_NOT_AGREE;
    }
    return SeoulMileageSupportedType.SUPPORTED_AGREE;
  }

  const seoulMileageSupportedType = getSeoulMileageSupportedType(vehicleInfo.permissions[0].features);

  function getFeatureAgreed(permissionType: PermissionTypeKr) {
    const permission = vehicleInfo.permissions[0].features.find(({id}) => id === permissionType);

    if (!permission) {
      return false;
    }

    return !!permission.agreedAt;
  }

  function getFeatureAgreeDate(permissionType: PermissionTypeKr) {
    const permission = vehicleInfo.permissions[0].features.find(({id}) => id === permissionType);

    if (!permission) {
      return null;
    }

    return permission.agreedAt;
  }

  return {
    refetchAvailableComponent,
    checkAvailableComponent,
    isAgreeFeature,
    isSupportFeature,
    isCcsFullEnroll: vehicleInfo.ccsFullEnrollYn,
    isMainSupport: vehicleInfo.mainSupportYn,
    isPrimaryUserYn: vehicleInfo.primaryUserYn,
    role: vehicleInfo.role.code,
    seoulMileageSupportedType,
    getFeatureAgreed,
    getFeatureAgreeDate,
  };
}
